import { useState, useEffect } from 'react';
import { useI18n } from '../../lib/i18n';
import { Drawer } from '../ui/Drawer';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { PillTabs } from '../ui/PillTabs';
import {
  RefreshIcon,
  CopyIcon,
  CheckIcon,
  PhoneIcon,
} from '../icons';
import {
  type ConnectedNumber,
  requestNumberCode,
  confirmNumberCode,
  saveIntegration,
  numberLabel,
} from '../../lib/whatsappApi';
import {
  getQualityRatingDisplay,
  formatMessagingLimitTier,
  getNameStatusDisplay,
} from '../../lib/connectHelpers';

export interface NumberDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  number: ConnectedNumber | null;
  onRefreshHealth: (id: string) => Promise<void>;
  onUpdated: () => Promise<void>;
}

export function NumberDetailsDrawer({
  open,
  onClose,
  number,
  onRefreshHealth,
  onUpdated,
}: NumberDetailsDrawerProps) {
  const { t } = useI18n();
  const [tab, setTab] = useState<'overview' | 'credentials' | 'verify'>('overview');
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Credentials edit state
  const [newToken, setNewToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [savingCreds, setSavingCreds] = useState(false);
  const [credsErr, setCredsErr] = useState<string | null>(null);
  const [credsSaved, setCredsSaved] = useState(false);

  // Verification state
  const [verifyPhone, setVerifyPhone] = useState('');
  const [verifyMethod, setVerifyMethod] = useState<'sms' | 'voice'>('sms');
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyStep, setVerifyStep] = useState<'request' | 'confirm' | 'done'>('request');
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyErr, setVerifyErr] = useState<string | null>(null);

  // Health refresh state
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (number?.verifiedPhoneE164) {
      setVerifyPhone(number.verifiedPhoneE164);
    }
  }, [number]);

  function copy(field: string, value: string) {
    if (!value) return;
    void navigator.clipboard?.writeText(value);
    setCopiedField(field);
    window.setTimeout(() => setCopiedField(null), 1500);
  }

  async function handleRefreshHealth() {
    if (!number) return;
    setRefreshing(true);
    try {
      await onRefreshHealth(number.id);
    } finally {
      setRefreshing(false);
    }
  }

  async function handleSaveCredentials() {
    if (!number) return;
    setSavingCreds(true);
    setCredsErr(null);
    setCredsSaved(false);
    try {
      await saveIntegration({
        phoneNumberId: number.phoneNumberId,
        businessAccountId: number.businessAccountId || '',
        token: newToken.trim() || undefined,
        integrationId: number.id,
      });
      setNewToken('');
      setCredsSaved(true);
      await onUpdated();
      window.setTimeout(() => setCredsSaved(false), 2500);
    } catch (e) {
      setCredsErr((e as Error).message);
    } finally {
      setSavingCreds(false);
    }
  }

  async function handleRequestOtp() {
    if (!number) return;
    setVerifyBusy(true);
    setVerifyErr(null);
    try {
      await requestNumberCode({
        phoneE164: verifyPhone.trim() || number.verifiedPhoneE164 || '',
        codeMethod: verifyMethod === 'sms' ? 'SMS' : 'VOICE',
        integrationId: number.id,
      });
      setVerifyStep('confirm');
    } catch (e) {
      setVerifyErr((e as Error).message);
    } finally {
      setVerifyBusy(false);
    }
  }

  async function handleConfirmOtp() {
    if (!number) return;
    setVerifyBusy(true);
    setVerifyErr(null);
    try {
      await confirmNumberCode({
        code: verifyCode.trim(),
        integrationId: number.id,
      });
      setVerifyStep('done');
      await onUpdated();
    } catch (e) {
      setVerifyErr((e as Error).message);
    } finally {
      setVerifyBusy(false);
    }
  }

  if (!number) return null;

  const health = number.health;
  const quality = getQualityRatingDisplay(health?.qualityRating);
  const tier = formatMessagingLimitTier(health?.messagingLimitTier);
  const nameStatus = getNameStatusDisplay(health?.nameStatus);
  const phoneDisplay = health?.displayPhoneNumber || number.verifiedPhoneE164 || numberLabel(number);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={phoneDisplay}
      size="md"
    >
      <div className="space-y-4">
        {/* Top Header Card */}
        <div className="rounded-card border border-line bg-surface/30 p-4 dark:border-slate-800 dark:bg-slate-800/30">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-brand-50 text-brand dark:bg-brand-950/40 dark:text-brand-tint">
                <PhoneIcon size={18} />
              </span>
              <div>
                <h3 className="font-mono text-sm font-bold text-ink dark:text-slate-100" dir="ltr">
                  {phoneDisplay}
                </h3>
                <p className="text-xs text-ink-3 dark:text-slate-400">
                  {health?.verifiedName || number.label || t('connect.table.verifiedName')}
                </p>
              </div>
            </div>

            <Button
              size="sm"
              variant="secondary"
              disabled={refreshing}
              onClick={handleRefreshHealth}
              className="text-xs"
            >
              <RefreshIcon size={13} className={refreshing ? 'animate-spin me-1' : 'me-1'} />
              {t('connect.action.refreshHealth')}
            </Button>
          </div>

          <div className="mt-3 flex flex-wrap gap-2 pt-2 border-t border-line/60 dark:border-slate-800">
            <Badge variant={quality.variant} size="sm" dot={quality.dot}>
              {t(quality.labelKey)}
            </Badge>
            <Badge variant="neutral" size="sm">
              {tier.labelKey ? t(tier.labelKey) : tier.text}
            </Badge>
            <Badge variant={nameStatus.variant} size="sm">
              {t(nameStatus.labelKey)}
            </Badge>
            {number.isPaused && (
              <Badge variant="warning" size="sm" dot>
                {t('connect.waba.pausedBadge')}
              </Badge>
            )}
          </div>
        </div>

        {/* Tab switcher */}
        <PillTabs
          tabs={[
            { id: 'overview', label: t('connect.drawer.tabs.overview') },
            { id: 'credentials', label: t('connect.drawer.tabs.credentials') },
            { id: 'verify', label: t('connect.drawer.tabs.verify') },
          ]}
          activeId={tab}
          onChange={(id) => setTab(id as 'overview' | 'credentials' | 'verify')}
        />

        {/* Tab 1: Overview */}
        {tab === 'overview' && (
          <div className="space-y-3">
            <div className="rounded-card border border-line bg-white p-4 space-y-2.5 dark:border-slate-800 dark:bg-slate-900">
              <h4 className="text-xs font-bold uppercase tracking-wide text-ink-3 dark:text-slate-400">
                {t('connect.health.title')}
              </h4>
              <dl className="divide-y divide-line text-xs dark:divide-slate-800">
                <div className="flex justify-between py-2">
                  <dt className="text-ink-3 dark:text-slate-400">{t('connect.health.token')}</dt>
                  <dd className="font-semibold text-ink dark:text-slate-200">
                    {number.hasToken ? `✓ ${t('connect.health.ok')}` : `✗ ${t('connect.health.fail')}`}
                  </dd>
                </div>
                <div className="flex justify-between py-2">
                  <dt className="text-ink-3 dark:text-slate-400">{t('connect.health.webhook')}</dt>
                  <dd className="font-semibold text-ink dark:text-slate-200">
                    {number.isLive ? `✓ ${t('connect.health.ok')}` : `✗ ${t('connect.health.fail')}`}
                  </dd>
                </div>
                <div className="flex justify-between py-2">
                  <dt className="text-ink-3 dark:text-slate-400">Platform</dt>
                  <dd className="font-mono text-ink dark:text-slate-200">
                    {health?.platformType || 'CLOUD_API'}
                  </dd>
                </div>
                <div className="flex justify-between py-2">
                  <dt className="text-ink-3 dark:text-slate-400">Code Verification</dt>
                  <dd className="font-semibold text-ink dark:text-slate-200">
                    {health?.codeVerificationStatus || 'VERIFIED'}
                  </dd>
                </div>
                {health?.error && (
                  <div className="py-2 text-danger">
                    <dt className="text-ink-3">{t('connect.status.error')}</dt>
                    <dd className="mt-1 font-mono text-[11px] bg-danger-50 p-2 rounded dark:bg-rose-950/40">
                      {health.error}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Coexistence history sync card */}
            {number.coexistence && number.historySync && (
              <div className="rounded-card border border-sky-200 bg-sky-50/50 p-4 dark:border-sky-900/60 dark:bg-sky-950/30">
                <h4 className="text-xs font-bold text-sky-900 dark:text-sky-200">
                  📱 {t('connect.numbers.history')}
                </h4>
                <p className="mt-1 text-xs text-sky-800 dark:text-sky-300">
                  {t(`connect.numbers.historyStatus.${number.historySync.status}`)}
                  {number.historySync.status === 'in_progress' && ` (${number.historySync.progress}%)`}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Tab 2: Credentials & Webhook */}
        {tab === 'credentials' && (
          <div className="space-y-4">
            {/* Read-only IDs */}
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-semibold text-ink-2 dark:text-slate-300">
                  {t('connect.field.phoneId')}
                </label>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={number.phoneNumberId}
                    className="flex-1 rounded-[9px] border border-line bg-surface/40 px-3 py-2 font-mono text-xs text-ink outline-none dark:border-slate-800 dark:bg-slate-800/40 dark:text-slate-200"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => copy('phoneId', number.phoneNumberId)}
                  >
                    {copiedField === 'phoneId' ? <CheckIcon size={14} className="text-success" /> : <CopyIcon size={14} />}
                  </Button>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-ink-2 dark:text-slate-300">
                  {t('connect.field.wabaId')}
                </label>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={number.businessAccountId || ''}
                    className="flex-1 rounded-[9px] border border-line bg-surface/40 px-3 py-2 font-mono text-xs text-ink outline-none dark:border-slate-800 dark:bg-slate-800/40 dark:text-slate-200"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => copy('wabaId', number.businessAccountId || '')}
                  >
                    {copiedField === 'wabaId' ? <CheckIcon size={14} className="text-success" /> : <CopyIcon size={14} />}
                  </Button>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-ink-2 dark:text-slate-300">
                  {t('connect.webhook.url')}
                </label>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={number.webhookUrl || ''}
                    className="flex-1 rounded-[9px] border border-line bg-surface/40 px-3 py-2 font-mono text-xs text-ink outline-none dark:border-slate-800 dark:bg-slate-800/40 dark:text-slate-200"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => copy('webhookUrl', number.webhookUrl || '')}
                  >
                    {copiedField === 'webhookUrl' ? <CheckIcon size={14} className="text-success" /> : <CopyIcon size={14} />}
                  </Button>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-ink-2 dark:text-slate-300">
                  {t('connect.webhook.verify')}
                </label>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={number.verifyToken || ''}
                    className="flex-1 rounded-[9px] border border-line bg-surface/40 px-3 py-2 font-mono text-xs text-ink outline-none dark:border-slate-800 dark:bg-slate-800/40 dark:text-slate-200"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => copy('verifyToken', number.verifyToken || '')}
                  >
                    {copiedField === 'verifyToken' ? <CheckIcon size={14} className="text-success" /> : <CopyIcon size={14} />}
                  </Button>
                </div>
              </div>
            </div>

            {/* Token update box */}
            <div className="rounded-card border border-line bg-white p-4 space-y-3 dark:border-slate-800 dark:bg-slate-900">
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-ink-2 dark:text-slate-300">
                  {t('connect.field.token')}
                </span>
                <div className="flex gap-2">
                  <input
                    type={showToken ? 'text' : 'password'}
                    value={newToken}
                    onChange={(e) => setNewToken(e.target.value)}
                    placeholder={number.hasToken ? '••••••••••••••••' : ''}
                    className="min-w-0 flex-1 rounded-[9px] border border-line bg-white px-3 py-2 font-mono text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowToken(!showToken)}
                  >
                    {showToken ? t('connect.token.hide') : t('connect.token.show')}
                  </Button>
                </div>
                {number.hasToken && (
                  <span className="mt-1 block text-[11px] text-ink-3 dark:text-slate-400">
                    {t('connect.tokenSaved')}
                  </span>
                )}
              </label>

              {credsErr && (
                <div className="rounded-[9px] bg-danger-50 px-3 py-2 text-xs font-semibold text-danger dark:bg-rose-950/40 dark:text-rose-300">
                  {credsErr}
                </div>
              )}
              {credsSaved && (
                <div className="rounded-[9px] bg-success-50 px-3 py-2 text-xs font-semibold text-success dark:bg-emerald-950/40 dark:text-emerald-300">
                  ✓ {t('common.saved')}
                </div>
              )}

              <Button
                size="sm"
                disabled={savingCreds || !newToken.trim()}
                onClick={handleSaveCredentials}
              >
                {savingCreds ? t('connect.saving') : t('connect.save')}
              </Button>
            </div>
          </div>
        )}

        {/* Tab 3: Phone Verification (OTP) */}
        {tab === 'verify' && (
          <div className="rounded-card border border-line bg-white p-4 space-y-4 dark:border-slate-800 dark:bg-slate-900">
            <div>
              <h4 className="text-sm font-extrabold text-ink dark:text-slate-100">
                {t('connect.verify.title')}
              </h4>
              <p className="mt-0.5 text-xs text-ink-3 dark:text-slate-400">
                {t('connect.verify.body')}
              </p>
            </div>

            {verifyErr && (
              <div className="rounded-[9px] bg-danger-50 px-3 py-2 text-xs font-semibold text-danger dark:bg-rose-950/40 dark:text-rose-300">
                {verifyErr}
              </div>
            )}

            {verifyStep === 'done' ? (
              <div className="rounded-[9px] bg-success-50 px-3 py-3 text-sm font-bold text-success dark:bg-emerald-950/40 dark:text-emerald-400">
                ✓ {t('connect.verify.done')}
              </div>
            ) : verifyStep === 'request' ? (
              <div className="space-y-3">
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-ink-2 dark:text-slate-300">
                    {t('connect.verify.phone')}
                  </span>
                  <input
                    value={verifyPhone}
                    onChange={(e) => setVerifyPhone(e.target.value)}
                    placeholder="+96812345678"
                    className="w-full rounded-[9px] border border-line bg-white px-3 py-2 font-mono text-sm text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
                  />
                </label>

                <div>
                  <span className="mb-1 block text-xs font-semibold text-ink-2 dark:text-slate-300">
                    Method
                  </span>
                  <select
                    value={verifyMethod}
                    onChange={(e) => setVerifyMethod(e.target.value as 'sms' | 'voice')}
                    className="w-full rounded-[9px] border border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
                  >
                    <option value="sms">{t('connect.method.sms')}</option>
                    <option value="voice">{t('connect.method.voice')}</option>
                  </select>
                </div>

                <Button
                  onClick={handleRequestOtp}
                  disabled={!verifyPhone.trim() || verifyBusy}
                  className="w-full"
                >
                  {verifyBusy ? t('connect.verify.sending') : t('connect.verify.send')}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-ink-2 dark:text-slate-300">
                    Verification Code
                  </span>
                  <input
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value)}
                    placeholder="000000"
                    inputMode="numeric"
                    className="w-full rounded-[9px] border border-line bg-white px-3 py-2 font-mono text-sm text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
                  />
                </label>

                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => setVerifyStep('request')}
                    className="flex-1"
                  >
                    {t('connect.verify.back')}
                  </Button>
                  <Button
                    onClick={handleConfirmOtp}
                    disabled={!verifyCode.trim() || verifyBusy}
                    className="flex-1"
                  >
                    {verifyBusy ? t('connect.verify.confirming') : t('connect.verify.confirm')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Drawer>
  );
}

export default NumberDetailsDrawer;
